import { Helmet } from "react-helmet";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, Package, Truck, Clock, Shield, CreditCard, FileText } from "lucide-react";

const FAQTransportColisMaroc = () => {
  const faqCategories = [
    {
      title: "Général",
      icon: HelpCircle,
      questions: [
        {
          question: "Qu'est-ce que GoBledPro ?",
          answer: "GoBledPro est une plateforme de mise en relation entre des clients souhaitant envoyer des colis entre la France et le Maroc, et des transporteurs professionnels indépendants vérifiés. Nous ne sommes pas un transporteur, mais un intermédiaire qui facilite la mise en contact et offre un cadre sécurisé pour vos envois."
        },
        {
          question: "GoBledPro est-il un transporteur ?",
          answer: "Non, GoBledPro n'est pas un transporteur. Nous sommes une plateforme qui référence des transporteurs professionnels indépendants. Le contrat de transport est conclu directement entre vous et le transporteur que vous choisissez."
        },
        {
          question: "Comment fonctionne la plateforme ?",
          answer: "Vous décrivez votre envoi (type de colis, villes de départ et d'arrivée, date souhaitée), puis vous recevez des propositions de transporteurs vérifiés. Vous choisissez l'offre qui vous convient, vous convenez des détails avec le transporteur, et votre colis est acheminé."
        }
      ]
    },
    {
      title: "Envoi de colis",
      icon: Package,
      questions: [
        {
          question: "Quels types de colis puis-je envoyer ?",
          answer: "Vous pouvez envoyer des effets personnels, des vêtements, des cadeaux, des appareils électroniques (hors batteries lithium en vrac), du mobilier de petite taille, des livres, et bien d'autres articles. Certains produits sont interdits ou réglementés (voir les restrictions douanières)."
        },
        {
          question: "Quels produits sont interdits au transport ?",
          answer: "Sont interdits : les substances illicites, les armes, les matières dangereuses ou inflammables, les denrées périssables sans emballage adapté, les contrefaçons, et certains médicaments sans ordonnance. Consultez les réglementations douanières françaises et marocaines pour plus de détails."
        },
        {
          question: "Comment emballer mon colis ?",
          answer: "Utilisez un carton solide ou une valise rigide, protégez les objets fragiles avec du papier bulle ou du papier kraft, et fermez le colis de manière sécurisée. Étiquetez clairement avec le nom et l'adresse du destinataire, ainsi qu'un numéro de téléphone au Maroc."
        },
        {
          question: "Quel poids maximum puis-je envoyer ?",
          answer: "Le poids maximum dépend du transporteur et de son véhicule. La plupart acceptent des colis jusqu'à 50 kg. Pour des envois plus lourds ou volumineux, contactez directement le transporteur pour vérifier ses capacités."
        }
      ]
    },
    {
      title: "Transporteurs",
      icon: Truck,
      questions: [
        {
          question: "Comment les transporteurs sont-ils vérifiés ?",
          answer: "Nous vérifions l'identité du transporteur (pièce d'identité), ses documents professionnels (permis, carte grise, licence de transport le cas échéant), et son assurance. Nous privilégions également les transporteurs ayant une expérience sur le corridor France-Maroc."
        },
        {
          question: "Puis-je choisir mon transporteur ?",
          answer: "Oui, vous recevez plusieurs propositions et vous êtes libre de choisir le transporteur qui correspond le mieux à vos critères (prix, délai, avis, zone de desserte)."
        },
        {
          question: "Les transporteurs sont-ils assurés ?",
          answer: "Nous vérifions que les transporteurs disposent d'une assurance responsabilité civile professionnelle. Pour les envois de valeur, nous recommandons de souscrire une assurance complémentaire ou de vérifier avec le transporteur la couverture proposée."
        }
      ]
    },
    {
      title: "Délais et livraison",
      icon: Clock,
      questions: [
        {
          question: "Quels sont les délais de livraison ?",
          answer: "Les délais varient selon le mode de transport : 5 à 10 jours pour le transport routier, 2 à 5 jours pour l'aérien, et 10 à 21 jours pour le maritime. Ces délais sont indicatifs et peuvent varier selon la période et les formalités douanières."
        },
        {
          question: "Mon colis peut-il être livré partout au Maroc ?",
          answer: "La plupart des transporteurs desservent les grandes villes (Casablanca, Rabat, Marrakech, Fès, Tanger, Agadir) et de nombreuses villes moyennes. Pour les zones rurales ou éloignées, vérifiez avec le transporteur sa zone de desserte."
        },
        {
          question: "Puis-je suivre mon colis ?",
          answer: "Le suivi dépend du transporteur. Beaucoup proposent un suivi par SMS ou WhatsApp. Nous travaillons à généraliser un système de suivi standardisé sur la plateforme."
        }
      ]
    },
    {
      title: "Prix et paiement",
      icon: CreditCard,
      questions: [
        {
          question: "Comment sont calculés les prix ?",
          answer: "Les prix dépendent du poids, du volume, des villes de départ et d'arrivée, du délai souhaité, et de la période de l'année. Chaque transporteur fixe ses propres tarifs. GoBledPro vous permet de comparer plusieurs offres."
        },
        {
          question: "Y a-t-il des frais cachés ?",
          answer: "Les transporteurs référencés sur GoBledPro s'engagent à afficher des prix clairs. Cependant, des frais de douane peuvent s'appliquer à l'arrivée au Maroc selon la valeur déclarée de vos envois. Demandez au transporteur de préciser ce qui est inclus dans le prix."
        },
        {
          question: "Comment se fait le paiement ?",
          answer: "Les modalités de paiement sont convenues directement avec le transporteur. La plupart acceptent le virement, le paiement en espèces à la collecte, ou le paiement mobile. Nous recommandons de ne pas payer l'intégralité à l'avance."
        },
        {
          question: "GoBledPro prend-il une commission ?",
          answer: "Notre modèle économique repose sur un abonnement ou une commission auprès des transporteurs. Le prix affiché est le prix final pour le client, sans surcoût lié à notre plateforme."
        }
      ]
    },
    {
      title: "Douanes et réglementation",
      icon: FileText,
      questions: [
        {
          question: "Quels documents faut-il pour envoyer un colis au Maroc ?",
          answer: "Pour un envoi personnel, une liste du contenu avec la valeur déclarée est généralement suffisante. Pour des envois commerciaux ou de valeur importante, une facture pro-forma peut être demandée. Le transporteur peut vous guider sur les formalités."
        },
        {
          question: "Y a-t-il des droits de douane à payer ?",
          answer: "Les envois personnels peuvent être soumis à des droits de douane à l'arrivée au Maroc, selon la valeur déclarée et la nature des produits. Les taux varient. Renseignez-vous auprès des services douaniers marocains pour connaître les franchises applicables."
        },
        {
          question: "Que se passe-t-il si mon colis est bloqué en douane ?",
          answer: "En cas de blocage, le destinataire sera contacté pour fournir des documents complémentaires ou payer des droits. Le transporteur peut vous accompagner dans ces démarches. C'est pourquoi il est important de déclarer correctement le contenu de vos colis."
        }
      ]
    },
    {
      title: "Problèmes et réclamations",
      icon: Shield,
      questions: [
        {
          question: "Que faire si mon colis est perdu ou endommagé ?",
          answer: "Contactez d'abord le transporteur pour signaler le problème. Si vous avez souscrit une assurance, engagez la procédure de réclamation. Vous pouvez également nous contacter pour médiation. Conservez toutes les preuves (photos, échanges)."
        },
        {
          question: "Comment contacter GoBledPro en cas de problème ?",
          answer: "Vous pouvez nous joindre par email à contact@gobledpro.com ou via le formulaire de contact sur notre site. Nous répondons généralement sous 24 à 48 heures."
        },
        {
          question: "GoBledPro est-il responsable en cas de litige ?",
          answer: "GoBledPro est une plateforme de mise en relation. Le contrat de transport est conclu entre vous et le transporteur. En cas de litige, nous pouvons intervenir en médiation et suspendre un transporteur en cas de manquements répétés, mais nous ne sommes pas partie au contrat de transport."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>FAQ Transport Colis France Maroc – Questions Fréquentes | GoBledPro</title>
        <meta 
          name="description" 
          content="Toutes les réponses à vos questions sur le transport de colis entre la France et le Maroc : prix, délais, douanes, assurance. FAQ complète GoBledPro." 
        />
        <link rel="canonical" href="https://gobledpro.com/faq-transport-colis-maroc" />
      </Helmet>

      <Header />

      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <header className="text-center mb-12">
              <span className="inline-block text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full mb-4">
                FAQ
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
                Questions Fréquentes sur le Transport France – Maroc
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                Retrouvez toutes les réponses à vos questions sur l'envoi de colis entre la France et le Maroc.
              </p>
            </header>

            {/* FAQ Categories */}
            <div className="space-y-8">
              {faqCategories.map((category, categoryIndex) => (
                <section key={categoryIndex} className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <category.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="text-xl font-heading font-bold text-foreground">{category.title}</h2>
                  </div>
                  <Accordion type="single" collapsible className="space-y-2">
                    {category.questions.map((faq, faqIndex) => (
                      <AccordionItem 
                        key={faqIndex} 
                        value={`${categoryIndex}-${faqIndex}`}
                        className="bg-card border border-border rounded-xl px-4"
                      >
                        <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline py-4">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground pb-4">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </section>
              ))}
            </div>

            {/* Contact CTA */}
            <section className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center mt-12 mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                Vous n'avez pas trouvé la réponse à votre question ?
              </h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Notre équipe est là pour vous aider. Contactez-nous et nous vous répondrons dans les meilleurs délais.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a 
                  href="mailto:contact@gobledpro.com" 
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
                >
                  Nous contacter
                </a>
                <Link 
                  to="/demande-transport" 
                  className="text-primary hover:underline font-medium flex items-center gap-1"
                >
                  Demander un transport →
                </Link>
              </div>
            </section>

            {/* Liens utiles */}
            <section className="bg-muted/30 rounded-2xl p-6 md:p-8 mb-12">
              <h2 className="text-xl font-heading font-bold text-foreground mb-4">Liens utiles</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Link 
                  to="/transport-colis-france-maroc" 
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Package className="w-4 h-4" />
                  <span>Guide du transport de colis</span>
                </Link>
                <Link 
                  to="/prix-transport-colis-maroc" 
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <CreditCard className="w-4 h-4" />
                  <span>Tarifs et estimation de prix</span>
                </Link>
                <Link 
                  to="/transporteurs-verifies-maroc" 
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Truck className="w-4 h-4" />
                  <span>Nos transporteurs vérifiés</span>
                </Link>
                <Link 
                  to="/transport-colis-maroc-fiable" 
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Shield className="w-4 h-4" />
                  <span>Transport fiable et sécurisé</span>
                </Link>
              </div>
            </section>

            {/* Retour */}
            <div className="pt-8 border-t border-border">
              <Link 
                to="/" 
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                ← Retour à l'accueil
              </Link>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQTransportColisMaroc;
